function normalizeDependencyKey(key: string) {
  const match = key.match(/^\[["']?([^\]"']+)["']?\]\.([^.]+)\.([^.]+)$/);
  return match ? `['${match[1]}'].${match[2]}.${match[3]}` : '';
}

function parseDependencyKey(key: string): { group?: string; resource?: string; type?: string } {
  const match = key.match(/^\[["']?([^\]"']+)["']?\]\.([^.]+)\.([^.]+)$/);
  if (!match) return {};
  return { group: match[1], resource: match[2], type: match[3] };
}

/** 将 onCheck key 归一为表单单引号 path，避免与 FormItem / defaultData 不一致 */
export function toSingleQuoteDependencyKey(key: string) {
  const normalized = normalizeDependencyKey(key);
  return normalized || key;
}

/** onUnCheck 的 key 同样归一为单引号，否则清不掉 FormItem 已写入的值 */
export function normalizeRelativeFieldValues(values: Record<string, boolean | undefined> | undefined) {
  if (!values) return {};
  const result: Record<string, boolean | undefined> = {};
  for (const [key, value] of Object.entries(values)) {
    result[toSingleQuoteDependencyKey(key)] = value;
  }
  return result;
}

/**
 * 查找 roleMap 中对应权限项的 onCheck。
 * 同一 resource+verb 可能既是某页可见勾选项、又是他页隐藏联动项：优先取 hidden 定义，
 * 避免展开时误带上产品 SKU / 原子资源等页面的 list 依赖。
 */
function findVerbOnCheck(roleMap: any[], group?: string, resource?: string, type?: string) {
  if (!group || !resource || !type) return undefined;
  let visibleOnCheck: Record<string, boolean | undefined> | undefined;

  for (const item of roleMap || []) {
    for (const block of item?.list || []) {
      for (const verb of block?.list || []) {
        const verbGroup = verb.group || block.group || item.group;
        const verbResource = verb.resource || block.resource || item.resource;
        if (verbGroup === group && verbResource === resource && verb.type === type) {
          const onCheck = verb.relative?.onCheck as Record<string, boolean | undefined> | undefined;
          if (verb.hidden) {
            return onCheck;
          }
          if (visibleOnCheck === undefined) {
            visibleOnCheck = onCheck;
          }
        }
      }
    }
  }
  return visibleOnCheck;
}

/** 同 listBlock 内是否存在对应的可见勾选项 */
function isVisibleVerbInBlock(block: any, item: any, group?: string, resource?: string, type?: string) {
  if (!group || !resource || !type) return false;
  return (block?.list || []).some((candidate: any) => {
    if (candidate.hidden) return false;
    const candidateGroup = candidate.group || block.group || item.group;
    const candidateResource = candidate.resource || block.resource || item.resource;
    return candidateGroup === group && candidateResource === resource && candidate.type === type;
  });
}

/** 仍被其他已选可见权限的 onCheck 联动授予时，勿剥离该 key */
function isProtectedAutoGrant(data: Record<string, boolean>, roleMap: any[], targetKey: string) {
  for (const item of roleMap || []) {
    for (const block of item?.list || []) {
      for (const verb of block?.list || []) {
        if (verb.hidden) continue;
        const group = verb.group || block.group || item.group;
        const resource = verb.resource || block.resource || item.resource;
        const mainKey = `['${group}'].${resource}.${verb.type}`;
        if (!data[mainKey]) continue;
        const onCheck = verb.relative?.onCheck || {};
        if (Object.keys(onCheck).some((dep) => normalizeDependencyKey(dep) === targetKey)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * 递归展开 onCheck：勾选「更新」时会联动 get，也需一并写入 get 的 Tab 依赖，
 * 否则保存后再打开会被 removeSelectionsWithMissingDependencies 清掉。
 * 写出的 key 统一为单引号，与 FormItem / defaultData 对齐。
 */
export function expandRelativeOnCheck(
  roleMap: any[],
  onCheck: Record<string, boolean | undefined> | undefined,
  visited = new Set<string>(),
): Record<string, boolean | undefined> {
  if (!onCheck) return {};
  const result: Record<string, boolean | undefined> = {};

  for (const [key, value] of Object.entries(onCheck)) {
    result[toSingleQuoteDependencyKey(key)] = value;
  }

  for (const [key, value] of Object.entries(onCheck)) {
    const normalizedKey = toSingleQuoteDependencyKey(key);
    if (!value || visited.has(normalizedKey)) continue;
    visited.add(normalizedKey);
    const { group, resource, type } = parseDependencyKey(key);
    const nested = findVerbOnCheck(roleMap, group, resource, type);
    if (nested) {
      Object.assign(result, expandRelativeOnCheck(roleMap, nested, visited));
    }
  }
  return result;
}

function buildPermissionKey(group: string, resource: string, type: string) {
  return `['${group}'].${resource}.${type}`;
}

function hasAllDependencies(data: Record<string, boolean>, dependencies: string[]) {
  return dependencies.every((dependency) => data[normalizeDependencyKey(dependency)]);
}

/**
 * 按当前权限配置校验主权限依赖，避免编辑旧角色时显示不完整的勾选状态。
 * - 隐藏项不参与「依赖不全则清主权限」
 * - 仅校验同块内可见勾选项依赖（隐藏联动 API 如 skus.get 不作为回显门槛）
 * - 同一 Casbin key 在多菜单块重复出现时，任一配置项依赖满足即保留
 * - 若某 key 仍被其他已选可见权限联动授予，则保留（避免订单详情的 Tab 接口被产品页规则误删）
 */
export function removeSelectionsWithMissingDependencies(data: Record<string, boolean>, roleMap: any[]) {
  const verbsByKey = new Map<string, { dependencies: string[] }[]>();

  for (const item of roleMap) {
    for (const block of item?.list || []) {
      for (const verb of block?.list || []) {
        if (verb.hidden) continue;

        const group = verb.group || block.group || item.group;
        const resource = verb.resource || block.resource || item.resource;
        const key = buildPermissionKey(group, resource, verb.type);
        const dependencies = Object.keys(verb.relative?.onCheck || {}).filter((dependency) => {
          const parsed = parseDependencyKey(dependency);
          return isVisibleVerbInBlock(block, item, parsed.group, parsed.resource, parsed.type);
        });

        if (!verbsByKey.has(key)) {
          verbsByKey.set(key, []);
        }
        verbsByKey.get(key)!.push({ dependencies });
      }
    }
  }

  for (const [key, verbConfigs] of verbsByKey) {
    if (!data[key]) {
      continue;
    }

    const anyConfigSatisfied = verbConfigs.some(({ dependencies }) => hasAllDependencies(data, dependencies));
    if (!anyConfigSatisfied) {
      // 被他页已选权限联动授予时，不要因本页可见依赖不全而剥离
      if (isProtectedAutoGrant(data, roleMap, key)) continue;
      delete data[key];
    }
  }

  return data;
}
